import { Dataset, Document } from '@app/modules/core/store/models';
import { Layer } from '../../clustering';
import { MapDrawingContext } from '../../models';
import { StandardFeature } from '../standard/standard.feature';
import { FeatureRenderingUtilityService } from '../feature-rendering-utility.service';
import { PolygonOverlay } from '../polygon-overlay.model';
import { TopoOverlayView } from '@app/core/classes/overlay-view';

class StripedPolygonOverlay extends TopoOverlayView implements PolygonOverlay {
  private fillColor: string;
  private opacity: number;
  private strokeColor: string;

  private svgPath: SVGPathElement;
  private overlayDiv: HTMLDivElement;

  private paths: google.maps.LatLng[][] | google.maps.LatLng[] | any[];
  private bounds: google.maps.LatLngBounds;

  private _map: google.maps.Map;
  private preventDrawing: boolean;
  mapListeners: Record<string, google.maps.MapsEventListener>;

  private layerUId: string;

  constructor(options: google.maps.PolygonOptions, layerUId: string) {
    super();

    const { paths = [], fillColor, strokeColor, fillOpacity = 0.45, map } = options;

    const bounds = new google.maps.LatLngBounds();

    for (let i = 0; i < paths[0].length; i++) {
      bounds.extend(paths[0][i]);
    }

    this.bounds = bounds;
    this.overlayDiv = null;
    this.paths = paths as any[];
    this.fillColor = fillColor;
    this.strokeColor = strokeColor;
    this.opacity = fillOpacity;
    this._map = map;

    this.layerUId = layerUId;

    this.setMap(map);
  }

  setVisible(visible: boolean): void {
    if (Boolean(this.overlayDiv)) {
      this.overlayDiv.style.visibility = visible ? 'visible' : 'hidden';
    }
  }

  onAdd(): void {
    // create the DIV and set some basic attributes.
    const div = document.createElement('div');
    div.style.borderStyle = 'none';
    div.style.borderWidth = '0px';
    div.style.position = 'absolute';

    // create the svg element
    const svgns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgns, 'svg');
    svg.setAttributeNS(null, 'preserveAspectRatio', 'xMidYMid meet');

    const def = document.createElementNS(svgns, 'defs');

    // create the pattern fill
    const pattern = document.createElementNS(svgns, 'pattern');
    pattern.setAttributeNS(null, 'id', this.layerUId);
    pattern.setAttributeNS(null, 'patternUnits', 'userSpaceOnUse');
    pattern.setAttributeNS(null, 'patternTransform', 'rotate(-45)');
    pattern.setAttributeNS(null, 'height', '8');
    pattern.setAttributeNS(null, 'width', '8');
    def.appendChild(pattern);

    const rect = document.createElementNS(svgns, 'rect');
    rect.setAttributeNS(null, 'id', 'rectFill');
    rect.setAttributeNS(null, 'fill', this.fillColor);
    rect.setAttributeNS(null, 'fill-opacity', '1');
    rect.setAttributeNS(null, 'stroke', this.strokeColor);
    rect.setAttributeNS(null, 'stroke-dasharray', '8,8');
    rect.setAttributeNS(null, 'stroke-width', '4');
    rect.setAttributeNS(null, 'stroke-opacity', '1');
    rect.setAttributeNS(null, 'height', '8');
    rect.setAttributeNS(null, 'width', '8');
    pattern.appendChild(rect);

    svg.appendChild(def);

    const path = document.createElementNS(svgns, 'path');
    path.setAttributeNS(null, 'fill', `url(#${this.layerUId})`);
    path.setAttributeNS(null, 'fill-rule', 'evenodd');

    this.svgPath = path;
    svg.appendChild(this.svgPath);

    svg.style.height = '100%';
    svg.style.width = '100%';

    div.appendChild(svg);

    this.overlayDiv = div;
    this.overlayDiv.className = 'polygon';
    this.overlayDiv.style.opacity = this.opacity.toString();

    var panes = this.getPanes();
    panes.overlayLayer.appendChild(div);

    this.mapListeners = {
      dragstart: this._map.addListener('dragstart', () => {
        this.setVisible(false);
        this.preventDrawing = true;
      }),
      zoom_changed: this._map.addListener('zoom_changed', () => {
        this.setVisible(false);
        this.preventDrawing = true;
      }),
      idle: this._map.addListener('idle', () => {
        this.preventDrawing = false;
        this.draw();
        setTimeout(() => {
          if (!this.preventDrawing) {
            this.setVisible(this.isInViewport());
          }
        }, 50);
      })
    };
  }

  onRemove(): void {
    this.overlayDiv.parentNode.removeChild(this.overlayDiv);
    this.overlayDiv = null;
    Object.values(this.mapListeners).forEach(listener => listener.remove());
  }

  draw(): void {
    if (this.preventDrawing) {
      return;
    }

    var overlayProjection = this.getProjection();

    var sw = overlayProjection.fromLatLngToDivPixel(this.bounds.getSouthWest());
    var ne = overlayProjection.fromLatLngToDivPixel(this.bounds.getNorthEast());

    // Resize the image's DIV to fit the indicated dimensions.
    var div = this.overlayDiv;
    div.style.left = sw.x + 'px';
    div.style.top = ne.y + 'px';
    div.style.width = ne.x - sw.x + 'px';
    div.style.height = sw.y - ne.y + 'px';

    this.svgPath.setAttributeNS(null, 'd', this.adjustPoints());
  }

  private adjustPoints(): string {
    //adjust the polygon points based on the projection.
    const proj = this.getProjection();
    const sw = proj.fromLatLngToDivPixel(this.bounds.getSouthWest());
    const ne = proj.fromLatLngToDivPixel(this.bounds.getNorthEast());

    let adjustedCoords = '';
    for (var i = 0; i < this.paths.length; i++) {
      let points = '';
      for (var j = 0; j < this.paths[i].length; j++) {
        let point = proj.fromLatLngToDivPixel(this.paths[i][j]);
        if (j == 0) {
          points += point.x - sw.x + ', ' + (point.y - ne.y);
        } else {
          points += ' ' + (point.x - sw.x) + ', ' + (point.y - ne.y);
        }
      }
      adjustedCoords += 'M' + points + 'z ';
    }

    return adjustedCoords;
  }

  private isInViewport(): boolean {
    const mapBounds = this._map.getBounds();
    return this.paths.some(path => path.some(coord => mapBounds.contains(coord)));
  }
}

export class HorizonGeospatialThreatZoneFeature extends StandardFeature {
  addPolygon(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any, geoField: string): void {
    super.addPolygon(
      context,
      { ...layer, displayOptions: { ...layer.displayOptions, strokeOpacity: 0.5, fillOpacity: 0 } },
      dataset,
      document,
      geometry,
      geoField
    );

    const paths = FeatureRenderingUtilityService.normalizePolygonCoordinates(geometry.coordinates);

    const opacity = layer.displayOptions.shapeOpacity ?? 0.4;

    const fillColor = context.ratingsUtils.getRatingsTextAndColor(document.custom_rating_number ?? document.rating_number)?.color;
    const strokeColor =
      '#' +
      fillColor
        .replace(/^#/, '')
        .replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + 120)).toString(16)).substr(-2));

    const options = {
      paths,
      strokeColor,
      fillColor,
      strokeOpacity: opacity,
      fillOpacity: opacity,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    };

    const overlay = new StripedPolygonOverlay(options, layer.uId);
    overlay['__data'] = {
      documentUId: document.uid
    };
    this.googleMapFeatures.polygonOverlays.push(overlay);
  }
}
